import { Typography, useTheme, Box } from '@mui/material'
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  ChangeEvent,
  DragEvent,
  KeyboardEvent,
} from 'react'
import OutlinedButton, {
  OutlinedButtonVariant,
} from '../Buttons/OutlinedButton'
import { useAppSnackbar } from '../../hooks/useAppSnackbar'
import { useTranslation } from 'react-i18next'

// Define types
interface ImageObject {
  id: string
  file: File
  preview: string
}

interface ImageUploaderProps {
  initialImageBase64?: string
  onImageUpdate: (base64Data: string | null) => void
  initialFileName?: string
  title: string
  subtitle?: string
  isEditVariant?: boolean
  maxFileSize?: number // Maximum file size in bytes
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  initialImageBase64,
  onImageUpdate,
  title,
  subtitle,
  initialFileName = 'uploaded-image.jpg',
  isEditVariant = false,
  maxFileSize = 120 * 1024, // Default 120kb
}) => {
  const [image, setImage] = useState<ImageObject | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dropAreaRef = useRef<HTMLDivElement>(null)

  const { handleError } = useAppSnackbar()
  const { t } = useTranslation()
  const defaultImageUploaderError = t(
    'ImageUploader.FileUpload.Error',
    'Error occurred uploading image.'
  )

  const maxSizeForMessage =
    maxFileSize >= 1024 * 1024
      ? (maxFileSize / (1024 * 1024)).toFixed(1) + 'MB'
      : (maxFileSize / 1024).toFixed(0) + 'KB'

  const fileSizeError = t(
    'ImageUploader.FileSize.Error',
    `File size exceeds the maximum limit of {{maxSizeForMessage}}.`,
    { maxSizeForMessage }
  )

  const uploadHelperMessage = t(
    'ImageUploader.Usage.Edit.HelpText',
    'Drag & drop an image here, paste from clipboard, or click to browse. Max file size of {{maxSizeForMessage}}',
    { maxSizeForMessage }
  )

  const clickEditHelperMessage = t(
    'ImageUploader.Usage.NoEdit.HelpText',
    'Click Edit to upload image'
  )

  const theme = useTheme()

  // Initialize with base64 data if provided
  useEffect(() => {
    if (initialImageBase64 && !image) {
      // Convert base64 to blob
      const byteString = atob(
        initialImageBase64.split(',')[1] || initialImageBase64
      )
      const mimeType =
        initialImageBase64.split(',')[0]?.match(/:(.*?);/)?.[1] || 'image/jpeg'
      const ab = new ArrayBuffer(byteString.length)
      const ia = new Uint8Array(ab)

      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i)
      }

      const blob = new Blob([ab], { type: mimeType })
      const file = new File([blob], initialFileName, { type: mimeType })

      setImage({
        id: Date.now() + Math.random().toString(36).substring(2, 9),
        file,
        preview: URL.createObjectURL(blob),
      })
    }
  }, [initialImageBase64, initialFileName, image])

  // Convert file to base64 and call the update function
  const fileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        const result = reader.result as string
        // Strip the data URL prefix (e.g., "data:image/png;base64,") to get only the base64 string
        const base64String = result.split(',')[1] || result
        resolve(base64String)
      }
      reader.onerror = (error) => reject(error)
    })
  }, [])

  // Handle file selection - wrapped in useCallback to prevent recreation on every render
  const handleFileSelect = useCallback(
    async (files: FileList | null): Promise<void> => {
      if (!files || files.length === 0 || !isEditVariant) return

      // Get only the first file that is an image
      const file = Array.from(files).find((file) =>
        file.type.startsWith('image/')
      )

      if (!file) return

      // Check file size
      if (file.size > maxFileSize) {
        await handleError(null, fileSizeError)
        return
      }

      if (image) {
        URL.revokeObjectURL(image.preview)
      }

      // Convert to base64 and call the update function
      try {
        const base64Data = await fileToBase64(file)
        onImageUpdate(base64Data)

        // Set the new image
        setImage({
          id: Date.now() + Math.random().toString(36).substring(2, 9),
          file,
          preview: URL.createObjectURL(file),
        })
      } catch (error) {
        await handleError(error, defaultImageUploaderError)
      }
    },
    [
      image,
      onImageUpdate,
      fileToBase64,
      isEditVariant,
      maxFileSize,
      defaultImageUploaderError,
      fileSizeError,
      handleError,
    ]
  )

  // Handle file input change (traditional upload button)
  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (!isEditVariant) return
    handleFileSelect(e.target.files)
    // Reset file input to allow uploading the same file again
    e.target.value = ''
  }

  // Click handler for the drop area
  const handleAreaClick = (): void => {
    if (!isEditVariant) return
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  // Drag and drop handlers
  const handleDragEnter = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    if (!isEditVariant) return
    setIsDragging(true)
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    if (!isEditVariant) return
    if (!isDragging) setIsDragging(true)
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    if (!isEditVariant) return
    setIsDragging(false)
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    if (!isEditVariant) return
    setIsDragging(false)

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files)
    }
  }

  // Handle paste from clipboard
  useEffect(() => {
    if (!isEditVariant) return

    const handlePaste = (e: ClipboardEvent): void => {
      if (e.clipboardData && e.clipboardData.files.length > 0) {
        handleFileSelect(e.clipboardData.files)
      }
    }

    // Add the paste event listener to the document
    document.addEventListener('paste', handlePaste)

    // Clean up
    return () => {
      document.removeEventListener('paste', handlePaste)
    }
  }, [handleFileSelect, isEditVariant])

  // Clean up object URL when component unmounts
  useEffect(() => {
    return () => {
      if (image) {
        URL.revokeObjectURL(image.preview)
      }
    }
  }, [image])

  // Handle image removal
  const removeImage = (): void => {
    if (image) {
      URL.revokeObjectURL(image.preview)
      setImage(null)
      onImageUpdate(null) // Notify parent that image was removed
    }
  }

  // If isEditVariant is false, show simplified read-only view
  if (!isEditVariant) {
    return (
      <Box className="image-uploader-container">
        <Typography variant="h5">{title}</Typography>
        {image ? (
          <Box className="image-preview-container">
            <Box className="image-preview">
              <img src={image.preview} alt={`Preview of ${image.file.name}`} />
            </Box>
          </Box>
        ) : (
          <p>{clickEditHelperMessage}</p>
        )}
      </Box>
    )
  }

  return (
    <Box
      className="image-uploader-container"
      sx={{
        background: theme.palette.imageUploadBackground.main,
      }}
    >
      <Typography variant="h5">{title}</Typography>
      {!image ? (
        <Box
          ref={dropAreaRef}
          className={`drop-area ${isDragging ? 'dragging' : ''}`}
          onClick={handleAreaClick}
          onKeyDown={(e: KeyboardEvent) => {
            e.preventDefault()
          }}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
          aria-label="Upload image"
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileInputChange}
            accept="image/*"
            style={{ display: 'none' }}
          />
          <Box className="upload-message">
            {!!subtitle ? (
              <Typography variant="h6">{subtitle}</Typography>
            ) : undefined}
            <p>{uploadHelperMessage}</p>
            <OutlinedButton
              variant={OutlinedButtonVariant.BrowseFiles}
              id="remove-button"
              onClick={removeImage}
            />
          </Box>
        </Box>
      ) : (
        <Box className="image-preview-container">
          <Box className="image-preview">
            <img src={image.preview} alt={`Preview of ${image.file.name}`} />
            <Box className="image-info">
              <span className="image-name">{image.file.name}</span>
              <span className="image-size">
                {(image.file.size / 1024).toFixed(1)} KB
              </span>
            </Box>
            <OutlinedButton
              variant={OutlinedButtonVariant.Remove}
              id="remove-button"
              onClick={removeImage}
            />
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default ImageUploader
